import { useRouter } from 'next/router';
import { isNil, propOr } from 'ramda';
import { ME } from '@engage/members/models/Member';
import { getMember } from '@engage/members/selectors';
import { useReduxState } from '@engage/redux/hooks';
import { isOverviewLoaded } from './redux/overview/overview';
import { getMemberBasicInfo, isFullBasicInfo } from './selectors';
import { getViewedMemberUsernameOrIdFromPath } from './urls/overview';

/**
 * In Next.js static-export mode, `router.query` may hold template-string
 * placeholders (e.g. `'[memberId]'`) instead of the real route-segment value
 * on hard-loads.  When that happens we fall back to `window.location.pathname`
 * and extract the segment positionally.
 */
const resolveFromWindowLocation = (): string => {
  if (typeof window === 'undefined') {
    return ME;
  }
  // Path may be /[locale]/members/[memberId]/... or /members/[memberId]/...
  const segments = window.location.pathname.split('/').filter(Boolean);
  const membersIdx = segments.findIndex(s => s === 'members');
  return membersIdx !== -1 && segments[membersIdx + 1]
    ? decodeURIComponent(segments[membersIdx + 1])
    : ME;
};

export const useMemberId = (): string => {
  const router = useRouter();
  const memberIdOrUsername = getViewedMemberUsernameOrIdFromPath(router);

  // In Next.js static-export mode, router.query may be unreliable:
  //
  // 1. Template placeholder: router.query.memberId = '[memberId]' (pre-hydration)
  // 2. Undefined on locale pages: router.query.memberId = undefined because
  //    Next.js can't match /de-DE/members/allisonschloss/overview/ against
  //    the route pattern /members/[memberId]/overview (extra locale prefix).
  //    decodeURIComponent(undefined) yields the string 'undefined'.
  //
  // In both cases, fall back to extracting the real value from
  // window.location.pathname which always has the correct URL.
  const isPlaceholder =
    typeof memberIdOrUsername === 'string' &&
    memberIdOrUsername.startsWith('[') &&
    memberIdOrUsername.endsWith(']');
  const isUndefined = memberIdOrUsername === 'undefined';
  const isEmpty = !memberIdOrUsername;

  const resolvedId =
    isPlaceholder || isUndefined || isEmpty
      ? resolveFromWindowLocation()
      : memberIdOrUsername;

  const member = useReduxState(getMember, resolvedId);
  return propOr('', 'id', member);
};

export const useCanViewProfile = (memberId: string): boolean => {
  const basicInfo = useReduxState(getMemberBasicInfo, memberId);
  return !isNil(basicInfo) && isFullBasicInfo(basicInfo);
};

export const useIsPrivateProfile = (memberId: string): boolean => {
  const basicInfo = useReduxState(getMemberBasicInfo, memberId);
  return !isNil(basicInfo) && !isFullBasicInfo(basicInfo);
};

export const useIsMemberLoaded = (memberId: string): boolean => {
  const isPrivate = useIsPrivateProfile(memberId);
  const isLoaded = useReduxState(isOverviewLoaded, memberId);
  return isPrivate || isLoaded;
};
